import troopCatalog from "../../pages/battle-simulation/data/troopCatalog.generated.json";

const asInt = (value, fallback = 0) => {
  const parsed = Math.trunc(Number(value));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const titleCase = (value) => String(value || "")
  .trim()
  .replace(/[_-]+/g, " ")
  .replace(/\s+/g, " ")
  .replace(/\b\w/g, (char) => char.toUpperCase());

const TITAN_WTYPE_EMPRESS = 20;
const TITAN_WTYPE_SOURCE_SPIRIT = 21;

const canonicalFamilyDisplay = (family = {}) => {
  const wtype = asInt(family?.wtype, 0);
  if (wtype === TITAN_WTYPE_EMPRESS) {
    return {
      familyId: "femaleTitan",
      label: "Female Titan",
      icon: "/battle-sim-icons/femaleTitan.png",
    };
  }
  if (wtype === TITAN_WTYPE_SOURCE_SPIRIT) {
    return {
      familyId: "maleTitan",
      label: "Male Titan",
      icon: "/battle-sim-icons/maleTitan.png",
    };
  }
  return {
    familyId: String(family?.familyId || ""),
    label: String(family?.label || family?.familyId || "Troop").trim() || "Troop",
    icon: String(family?.iconPath || ""),
  };
};

const buildTroopDisplayByGid = () => {
  const lookup = {};
  const families = Array.isArray(troopCatalog?.families) ? troopCatalog.families : [];
  families.forEach((family, familyIndex) => {
    const canonicalFamily = canonicalFamilyDisplay(family);
    const familyLabel = canonicalFamily.label;
    const iconPath = canonicalFamily.icon;
    const baseOrder = Number(family?.order || familyIndex + 1);
    const rows = Array.isArray(family?.allRows) ? family.allRows : [];
    rows.forEach((row, rowIndex) => {
      const troopGid = asInt(row?.troopGid, 0);
      if (troopGid <= 0) return;
      lookup[String(troopGid)] = {
        troopName: String(row?.name || row?.tierLabel || familyLabel).trim() || familyLabel,
        icon: iconPath,
        familyId: canonicalFamily.familyId,
        tierLabel: String(row?.tierLabel || "").trim(),
        level: asInt(row?.level, null),
        reportOrder: baseOrder + (rowIndex / 1000),
        unitsPerTroop: Math.max(1, asInt(row?.stats?.population ?? row?.population ?? family?.defaultStats?.population, 1)),
      };
    });
    const defaultTroopGid = asInt(family?.defaultTroopGid, 0);
    if (defaultTroopGid > 0 && !lookup[String(defaultTroopGid)]) {
      lookup[String(defaultTroopGid)] = {
        troopName: familyLabel,
        icon: iconPath,
        familyId: canonicalFamily.familyId,
        tierLabel: "",
        level: null,
        reportOrder: baseOrder,
        unitsPerTroop: Math.max(1, asInt(family?.defaultStats?.population, 1)),
      };
    }
  });
  return lookup;
};

const TROOP_DISPLAY_BY_GID = buildTroopDisplayByGid();

export const getTroopUnitsPerCount = (troopGid) => {
  const display = TROOP_DISPLAY_BY_GID[String(asInt(troopGid, 0))] || {};
  return Math.max(1, asInt(display?.unitsPerTroop, 1));
};

export const getTroopTierNumber = (troopGid) => {
  const display = TROOP_DISPLAY_BY_GID[String(asInt(troopGid, 0))] || {};
  const match = String(display?.tierLabel || "").match(/T(\d+)/i);
  return match ? asInt(match[1], 0) : 0;
};

export const getActualUnitCountForTroops = (troops = []) => (
  (Array.isArray(troops) ? troops : []).reduce((sum, troop) => {
    const troopGid = asInt(troop?.troopGid, 0);
    const count = asInt(troop?.count, 0);
    if (troopGid <= 0 || count <= 0) return sum;
    return sum + (count * getTroopUnitsPerCount(troopGid));
  }, 0)
);

export const getParticipantActualUnitCount = (participant = {}) => {
  const fromTroops = getActualUnitCountForTroops(participant?.troops || []);
  if (fromTroops > 0) return fromTroops;
  return asInt(participant?.totalUnits, 0);
};

const formatAllianceTag = (participant = {}) => {
  const tag = String(participant?.allianceShort || "").trim();
  return tag ? `[${tag}]` : "";
};

const participantName = (participant = {}) => String(participant?.name || "").trim() || "Unknown Commander";

const formatTierLevel = (display = {}) => {
  const familyId = String(display?.familyId || "").toLowerCase();
  const level = asInt(display?.level, null);
  const tierLabel = String(display?.tierLabel || "").trim();
  if (familyId.includes("titan") || familyId.includes("warplane")) {
    return level != null && level >= 0 ? `Lv ${level + 1}` : (tierLabel || "-");
  }
  return tierLabel || "-";
};

const buildSideProfile = (participants = [], side) => {
  const sideParticipants = participants.filter((participant) => String(participant?.side || "") === side);
  if (!sideParticipants.length) return { name: side === "attacker" ? "Attacker" : "Defender" };
  const names = sideParticipants.map((participant) => participantName(participant));
  const tags = Array.from(new Set(sideParticipants.map(formatAllianceTag).filter(Boolean)));
  return {
    name: names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`,
    allianceTag: tags.join(" "),
  };
};

const aggregateTroopsForSide = (participants = [], side) => {
  const byGid = new Map();
  participants
    .filter((participant) => String(participant?.side || "") === side)
    .forEach((participant) => {
      (Array.isArray(participant?.troops) ? participant.troops : []).forEach((troop) => {
        const troopGid = asInt(troop?.troopGid, 0);
        const count = asInt(troop?.count, 0);
        if (troopGid <= 0 || count <= 0) return;
        const existing = byGid.get(troopGid) || { troopGid, count: 0 };
        existing.count += count;
        byGid.set(troopGid, existing);
      });
    });

  return Array.from(byGid.values())
    .map((row) => {
      const display = TROOP_DISPLAY_BY_GID[String(row.troopGid)] || {};
      const unitScale = getTroopUnitsPerCount(row.troopGid);
      const actualUnits = row.count * unitScale;
      return {
        troopGid: row.troopGid,
        troopName: display.troopName || "Unknown Troop",
        icon: display.icon || "",
        tierLevelLabel: formatTierLevel(display),
        reportOrder: Number(display.reportOrder || 999999),
        unitScale,
        troopCount: row.count,
        initialUnits: actualUnits,
        totalUnits: actualUnits,
        survivingUnits: null,
        woundedUnits: null,
        lostUnits: null,
        enemyUnitsKilled: null,
      };
    })
    .sort((left, right) => (
      Number(left.reportOrder || 999999) - Number(right.reportOrder || 999999)
      || String(left.troopName || "").localeCompare(String(right.troopName || ""))
    ));
};

const normalizeTroopRows = (troops = []) => (
  (Array.isArray(troops) ? troops : [])
    .map((troop) => {
      const troopGid = asInt(troop?.troopGid, 0);
      const count = asInt(troop?.count, 0);
      if (troopGid <= 0 || count <= 0) return null;
      const display = TROOP_DISPLAY_BY_GID[String(troopGid)] || {};
      const unitScale = getTroopUnitsPerCount(troopGid);
      const actualUnits = count * unitScale;
      return {
        troopGid,
        troopName: display.troopName || "Unknown Troop",
        icon: display.icon || "",
        tierLevelLabel: formatTierLevel(display),
        reportOrder: Number(display.reportOrder || 999999),
        unitScale,
        troopCount: count,
        initialUnits: actualUnits,
        totalUnits: actualUnits,
        survivingUnits: null,
        woundedUnits: null,
        lostUnits: null,
        enemyUnitsKilled: null,
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      Number(left.reportOrder || 999999) - Number(right.reportOrder || 999999)
      || String(left.troopName || "").localeCompare(String(right.troopName || ""))
    ))
);

const normalizeCapturedTroopRows = (rows = []) => (
  (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const troopGid = asInt(row?.troopGid || row?.gid, 0);
      const display = TROOP_DISPLAY_BY_GID[String(troopGid)] || {};
      const troopName = display.troopName || titleCase(row?.troopName || row?.name || "Unknown Troop");
      const survivingUnits = row?.survivingUnits == null ? null : asInt(row.survivingUnits, 0);
      const woundedUnits = row?.woundedUnits == null ? null : asInt(row.woundedUnits, 0);
      const lostUnits = row?.lostUnits == null ? null : asInt(row.lostUnits, 0);
      const inferredTotalUnits = [survivingUnits, woundedUnits, lostUnits]
        .reduce((sum, value) => sum + (value == null ? 0 : asInt(value, 0)), 0);
      const initialUnits = asInt(row?.initialUnits ?? row?.totalUnits, 0) || inferredTotalUnits;
      return {
        ...row,
        troopGid,
        troopName,
        icon: display.icon || row?.icon || "",
        tierLevelLabel: formatTierLevel(display),
        reportOrder: Number(display.reportOrder || row?.reportOrder || 999999),
        initialUnits,
        totalUnits: asInt(row?.totalUnits ?? row?.initialUnits, 0) || initialUnits,
        survivingUnits,
        woundedUnits,
        lostUnits,
        enemyUnitsKilled: row?.enemyUnitsKilled == null
          ? (row?.enemyUnitsKilled_kp == null ? null : asInt(row.enemyUnitsKilled_kp, 0))
          : asInt(row.enemyUnitsKilled, 0),
      };
    })
    .filter((row) => row.troopGid > 0)
    .sort((left, right) => (
      Number(left.reportOrder || 999999) - Number(right.reportOrder || 999999)
      || String(left.troopName || "").localeCompare(String(right.troopName || ""))
    ))
);

const participantIdentityKey = (participant = {}) => {
  const gameCommanderId = asInt(
    participant?.gameCommanderId
    || participant?.commanderId
    || participant?.commanderGameId,
    0,
  );
  return gameCommanderId > 0 ? String(gameCommanderId) : "";
};

const buildParticipantLookup = (participants = []) => {
  const lookup = new Map();
  (Array.isArray(participants) ? participants : []).forEach((participant) => {
    const key = participantIdentityKey(participant);
    if (key && !lookup.has(key)) lookup.set(key, participant);
  });
  return lookup;
};

const capturedRowIdentityKey = (row = {}) => {
  const commanderId = asInt(
    row?.commanderId
    || row?.gameCommanderId
    || row?.commanderGameId,
    0,
  );
  return commanderId > 0 ? String(commanderId) : "";
};

const buildCapturedTroopGroups = (rows = [], side, participants = []) => {
  const participantByCommander = buildParticipantLookup(participants);
  const byCommander = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const identityKey = capturedRowIdentityKey(row) || `unknown-${side}-${index}`;
    const participant = participantByCommander.get(identityKey) || {};
    const existing = byCommander.get(identityKey) || {
      identityKey,
      side,
      commanderId: capturedRowIdentityKey(row),
      name: String(row?.commanderName || participant?.name || "").trim() || "Unknown Commander",
      allianceShort: String(row?.allianceShort || participant?.allianceShort || "").trim(),
      cityLevel: asInt(row?.cityLevel ?? participant?.cityLevel, 0),
      commanderLevel: asInt(row?.commanderLevel ?? participant?.commanderLevel, 0),
      rows: [],
    };
    existing.rows.push(row);
    byCommander.set(identityKey, existing);
  });

  return Array.from(byCommander.values())
    .map((group) => {
      const totalUnits = group.rows.reduce((sum, row) => sum + asInt(row?.initialUnits ?? row?.totalUnits, 0), 0);
      const allianceTag = group.allianceShort ? `[${group.allianceShort}]` : "";
      const meta = [
        allianceTag,
        group.cityLevel > 0 ? `C${group.cityLevel}` : "",
        group.commanderLevel > 0 ? `Lv ${group.commanderLevel}` : "",
        `${totalUnits.toLocaleString()} units`,
      ].filter(Boolean).join(" | ");
      return {
        title: `${side === "attacker" ? "Attacker" : "Defender"} - ${group.name}${meta ? ` (${meta})` : ""}`,
        rows: group.rows,
      };
    })
    .sort((left, right) => String(left.title || "").localeCompare(String(right.title || "")));
};

const buildParticipantTroopGroups = (participants = [], side) => (
  participants
    .filter((participant) => String(participant?.side || "") === side)
    .map((participant) => {
      const rows = normalizeTroopRows(participant?.troops || []);
      if (!rows.length) return null;
      const allianceTag = formatAllianceTag(participant);
      const cityLevel = asInt(participant?.cityLevel, 0);
      const commanderLevel = asInt(participant?.commanderLevel, 0);
      const meta = [
        allianceTag,
        cityLevel > 0 ? `C${cityLevel}` : "",
        commanderLevel > 0 ? `Lv ${commanderLevel}` : "",
        `${getParticipantActualUnitCount(participant).toLocaleString()} units`,
      ].filter(Boolean).join(" | ");
      return {
        title: `${side === "attacker" ? "Attacker" : "Defender"} - ${participantName(participant)}${meta ? ` (${meta})` : ""}`,
        rows,
      };
    })
    .filter(Boolean)
);

const summarizeCapturedRows = (rows = []) => ({
  battlers: 0,
  totalUnits: rows.reduce((sum, row) => sum + asInt(row?.totalUnits ?? row?.initialUnits, 0), 0),
  survivingUnits: rows.reduce((sum, row) => sum + asInt(row?.survivingUnits, 0), 0),
  woundedUnits: rows.reduce((sum, row) => sum + asInt(row?.woundedUnits, 0), 0),
  lostUnits: rows.reduce((sum, row) => sum + asInt(row?.lostUnits, 0), 0),
  enemyUnitsKilled: rows.reduce((sum, row) => sum + asInt(row?.enemyUnitsKilled, 0), 0),
});

const isActualReportCaptureTruth = (captureTruth = {}, battle = {}) => {
  if (!captureTruth || typeof captureTruth !== "object") return false;
  if (captureTruth?.estimated === true || captureTruth?.estimate?.estimated === true) return false;
  const eventPoints = battle?.eventPoints || captureTruth?.eventPoints || {};
  if (eventPoints?.estimated === true || String(eventPoints?.status || "") === "estimated") return false;
  const estimateSource = String(
    captureTruth?.estimate?.source
    || captureTruth?.source
    || eventPoints?.source
    || "",
  ).toLowerCase();
  if (estimateSource.includes("estimate")) return false;
  return Number(captureTruth?.sourceAction || battle?.captureTruth?.sourceAction || 0) === 50009;
};

const buildSideSummary = (participants = [], side, rows = []) => {
  const sideParticipants = participants.filter((participant) => String(participant?.side || "") === side);
  const totalUnits = rows.reduce((sum, row) => sum + asInt(row?.initialUnits, 0), 0);
  return {
    battlers: sideParticipants.length,
    totalUnits,
    enemyUnitsKilled: null,
    lostUnits: null,
    woundedUnits: null,
    survivingUnits: null,
  };
};

export const buildBattleLogReportView = (battle = {}) => {
  const participants = Array.isArray(battle?.participants) ? battle.participants : [];
  const captureTruth = battle?.captureTruth && typeof battle.captureTruth === "object" ? battle.captureTruth : {};
  const capturedAttackerTroops = normalizeCapturedTroopRows(captureTruth.attackerTroops);
  const capturedDefenderTroops = normalizeCapturedTroopRows(captureTruth.defenderTroops);
  const hasCapturedRows = isActualReportCaptureTruth(captureTruth, battle)
    && (capturedAttackerTroops.length > 0 || capturedDefenderTroops.length > 0);
  if (!participants.length && !hasCapturedRows) return null;

  const attackerTroops = hasCapturedRows ? capturedAttackerTroops : aggregateTroopsForSide(participants, "attacker");
  const defenderTroops = hasCapturedRows ? capturedDefenderTroops : aggregateTroopsForSide(participants, "defender");
  const attackerProfile = buildSideProfile(participants, "attacker");
  const defenderProfile = buildSideProfile(participants, "defender");
  const attackerLabel = `${battle?.attacker?.allianceShort ? `[${battle.attacker.allianceShort}] ` : ""}${battle?.attacker?.name || attackerProfile.name || "Attacker"}`.trim();
  const defenderLabel = `${battle?.defender?.allianceShort ? `[${battle.defender.allianceShort}] ` : ""}${battle?.defender?.name || defenderProfile.name || "Defender"}`.trim();
  const attackerSummary = hasCapturedRows
    ? { ...summarizeCapturedRows(attackerTroops), ...(captureTruth.attackerSummary || {}) }
    : buildSideSummary(participants, "attacker", attackerTroops);
  const defenderSummary = hasCapturedRows
    ? { ...summarizeCapturedRows(defenderTroops), ...(captureTruth.defenderSummary || {}) }
    : buildSideSummary(participants, "defender", defenderTroops);

  return {
    title: `${attackerLabel || "Attacker"} vs ${defenderLabel || "Defender"}`,
    attackerProfile,
    defenderProfile,
    battleReport: {
      attackerSummary,
      defenderSummary,
      attackerTroops,
      defenderTroops,
      attackerTroopGroups: hasCapturedRows
        ? buildCapturedTroopGroups(capturedAttackerTroops, "attacker", participants)
        : buildParticipantTroopGroups(participants, "attacker"),
      defenderTroopGroups: hasCapturedRows
        ? buildCapturedTroopGroups(capturedDefenderTroops, "defender", participants)
        : buildParticipantTroopGroups(participants, "defender"),
      attackerBonuses: [],
      defenderBonuses: [],
    },
    diagnostics: {
      runtimeSource: titleCase(String(battle?.reportData?.battleType || "captured report")),
      reportId: "",
    },
  };
};
